import { useQuery, useMutation } from '@tanstack/react-query'
import { apiInstance } from 'services/api'
import { useMasquaradeToken } from 'services/masquarade/masquarade.hooks'
import { useAccessToken } from 'services/token/token.hooks'
import { useTermsOfUse } from 'services/terms/terms.hooks'
import type { ReservationStatus } from 'services/commonRooms/commonRooms.consts'

export interface CommonRoom {
  id: number
  name: string
  displayName: string
  description: string
  displayDescription: string
  equipment: {
    [key: string]: string[]
  }
  maxCapacity: number
  areaSqm: string
  mainPhoto: string | null
  rulesPdfUrl: string
  openingTime: string
  closingTime: string
  slotDurationMinutes: number
  maxBookingHours: number
  isActive: boolean
  isAvailableForBooking: boolean
}

export interface Reservation {
  id: number
  reservationCode: string
  userId: string
  commonRoom: CommonRoom
  commonRoomName: string
  contactName: string
  contactEmail: string
  contactPhone: string
  startDatetime: string
  endDatetime: string
  durationMinutes: number
  numberOfPeople: number
  purpose: string
  specialRequirements: string
  status: ReservationStatus
  totalCostPln: string
  totalCostEur: string
  cancelledAt: string | null
  checkInAt: string | null
  createdAt: string
  updatedAt: string
  ruleAcceptance: {
    acceptedAt: string
    rulesPdfUrlSnapshot: string
  }
}

export const useCommonRoomsList = () => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()
  const { hasUserConsented } = useTermsOfUse()

  return useQuery({
    queryKey: ['commonRoomsList', masquaradeToken],
    cacheTime: 0, //3600000,
    queryFn: () =>
      apiInstance<CommonRoom[]>({
        url: '/common-rooms/spaces/',
        method: 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data),
    enabled: !!accessToken && hasUserConsented
  })
}

export const useCommonRoom = (id: number) => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()
  const { hasUserConsented } = useTermsOfUse()

  return useQuery({
    queryKey: ['commonRoom', masquaradeToken, id],
    cacheTime: 0, //3600000,
    queryFn: () =>
      apiInstance<CommonRoom>({
        url: `/common-rooms/spaces/${id}/`,
        method: 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data),
    enabled: !!accessToken && hasUserConsented
  })
}

export interface TimeSlot {
  start: string // Time format "HH:mm:ss"
  end: string // Time format "HH:mm:ss"
  available: boolean
}

export interface AvailabilitySchedule {
  date: string // Date format "YYYY-MM-DD"
  slots: TimeSlot[]
}

export const useCommonRoomAvailability = (id: number, date?: string) => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()
  const { hasUserConsented } = useTermsOfUse()

  return useQuery({
    queryKey: ['commonRoomAvailability', masquaradeToken, id, date],
    cacheTime: 0, //3600000,
    queryFn: () =>
      apiInstance<AvailabilitySchedule>({
        url: `/common-rooms/spaces/${id}/availability/`,
        method: 'GET',
        params: date ? { date } : {},
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data),
    enabled: !!accessToken && hasUserConsented && !!id
  })
}

export const useCommonRoomsReservationsList = () => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()
  const { hasUserConsented } = useTermsOfUse()

  return useQuery({
    queryKey: ['commonRoomsReservationsList', masquaradeToken],
    cacheTime: 0, //3600000,
    queryFn: () =>
      apiInstance<Reservation[]>({
        url: '/common-rooms/reservations/',
        method: 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data),
    enabled: !!accessToken && hasUserConsented
  })
}

export const useCreateCommonRoomReservation = () => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()

  return useMutation({
    mutationFn: (reservation: {
      commonRoom: number
      contactName: string
      contactEmail: string
      contactPhone?: string
      startDatetime: string
      endDatetime: string
      numberOfPeople?: number
      purpose?: string
      specialRequirements?: string
      rulesAccepted: boolean
    }) =>
      apiInstance({
        url: '/common-rooms/reservations/',
        method: 'POST',
        data: reservation,
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data)
  })
}

export const useCommonRoomsReservation = (id: number) => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()
  const { hasUserConsented } = useTermsOfUse()

  return useQuery({
    queryKey: ['commonRoomsReservation', masquaradeToken, id],
    cacheTime: 0, //3600000,
    queryFn: () =>
      apiInstance<{
        commonRoom: number
        contactName: string
        contactEmail: string
        contactPhone: string
        startDatetime: string
        endDatetime: string
        numberOfPeople: number
        purpose: string
        specialRequirements: string
        rulesAccepted: boolean
      }>({
        url: `/common-rooms/reservations/${id}/`,
        method: 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data),
    enabled: !!accessToken && hasUserConsented
  })
}

export const useCancelCommonRoomReservation = (id: number) => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()

  return useMutation({
    mutationFn: (reason: string) =>
      apiInstance({
        url: `/common-rooms/reservations/${id}/cancel/`,
        method: 'POST',
        data: { reason },
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        }
      }).then(({ data }) => data)
  })
}
